import {
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import {
    ArrowUpDown,
    Check,
    ChevronLeft,
    ChevronRight,
    Edit3,
    Loader2,
    Merge,
    Minus,
    Plus,
    Save,
    Search,
    Tag,
    Tags,
    Trash2,
    X,
} from "lucide-preact";
import {
    type ReactElement,
    useMemo,
    useState,
} from "preact/compat";
import {
    toast,
} from "react-hot-toast";

import type {
    TagDefinition,
} from "@mrflark/spl_sdk";
import type {
    TagOperationAction,
    TagOperationRequest,
    TagStat,
} from "@mrflark/spl_service_model";
import {
    splClient,
} from "../../api/client.ts";

type SortField = "tag" | "count" | "description";
type SortDirection = "asc" | "desc";

type BulkAction = "add" | "remove_filtered" | "merge";

const BULK_ACTION_LABELS: Record<BulkAction, string> = {
    add: "Add tag to items with tag",
    remove_filtered: "Remove tag from items with tag",
    merge: "Merge tags into one",
};

const PAGE_SIZE = 25;

// inline rename form for individual tag rows
const InlineRenameForm = ({
    tag,
    onClose,
}: {
    tag: string;
    onClose: () => void;
}): ReactElement => {
    const queryClient = useQueryClient();
    const [newName, setNewName] = useState("");
    const [previewCount, setPreviewCount] = useState<number | null>(null);

    const previewMutation = useMutation({
        mutationFn: () =>
            splClient.previewTagOperation({
                action: "rename",
                tag,
                newTag: newName.trim(),
            }),
        onSuccess: (data) => setPreviewCount(data.affectedCount),
        onError: (error: Error) =>
            toast.error(`Preview failed: ${error.message}`),
    });

    const executeMutation = useMutation({
        mutationFn: () =>
            splClient.executeTagOperation({
                action: "rename",
                tag,
                newTag: newName.trim(),
            }),
        onSuccess: (data) => {
            toast.success(data.message);
            queryClient.invalidateQueries({
                queryKey: ["tagStats"],
            });
            onClose();
        },
        onError: (error: Error) =>
            toast.error(`Rename failed: ${error.message}`),
    });

    const trimmedName = newName.trim();
    const canPreview = trimmedName.length > 0 && trimmedName !== tag;

    return (
        <div className="flex items-center gap-2 mt-2">
            <input
                type="text"
                value={newName}
                onChange={(event) => {
                    setNewName((event.target as HTMLInputElement).value);
                    setPreviewCount(null);
                }}
                placeholder="New tag name..."
                className="px-2 py-1 text-sm bg-bg-tertiary border border-border rounded text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
                autoFocus
                onKeyDown={(event) => {
                    if (event.key === "Escape") onClose();
                }}
            />
            <button
                type="button"
                onClick={() => previewMutation.mutate()}
                disabled={!canPreview || previewMutation.isPending}
                className="px-2 py-1 text-xs bg-bg-tertiary border border-border rounded text-text-secondary hover:text-text-primary disabled:opacity-40 transition-colors"
            >
                {previewMutation.isPending
                    ? <Loader2 size={12} className="animate-spin" />
                    : "Preview"}
            </button>
            {previewCount !== null && (
                <>
                    <span className="text-xs text-text-secondary">
                        {previewCount} item{previewCount !== 1 ? "s" : ""}
                    </span>
                    <button
                        type="button"
                        onClick={() => executeMutation.mutate()}
                        disabled={executeMutation.isPending ||
                            previewCount === 0}
                        className="px-2 py-1 text-xs bg-accent text-white rounded hover:bg-accent/80 disabled:opacity-40 transition-colors"
                    >
                        {executeMutation.isPending
                            ? <Loader2 size={12} className="animate-spin" />
                            : "Rename"}
                    </button>
                </>
            )}
            <button
                type="button"
                onClick={onClose}
                className="p-1 text-text-muted hover:text-text-primary transition-colors"
            >
                <X size={14} />
            </button>
        </div>
    );
};

// inline confirmation for removing all instances of a tag
const InlineRemoveConfirm = ({
    tag,
    onClose,
}: {
    tag: string;
    onClose: () => void;
}): ReactElement => {
    const queryClient = useQueryClient();
    const [previewCount, setPreviewCount] = useState<number | null>(null);

    const previewMutation = useMutation({
        mutationFn: () =>
            splClient.previewTagOperation({
                action: "remove",
                tag,
            }),
        onSuccess: (data) => setPreviewCount(data.affectedCount),
        onError: (error: Error) =>
            toast.error(`Preview failed: ${error.message}`),
    });

    const executeMutation = useMutation({
        mutationFn: () =>
            splClient.executeTagOperation({
                action: "remove",
                tag,
            }),
        onSuccess: (data) => {
            toast.success(data.message);
            queryClient.invalidateQueries({
                queryKey: ["tagStats"],
            });
            onClose();
        },
        onError: (error: Error) =>
            toast.error(`Remove failed: ${error.message}`),
    });

    return (
        <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-red-400">
                Remove &quot;{tag}&quot; from all items?
            </span>
            {previewCount === null
                ? (
                    <button
                        type="button"
                        onClick={() => previewMutation.mutate()}
                        disabled={previewMutation.isPending}
                        className="px-2 py-1 text-xs bg-bg-tertiary border border-border rounded text-text-secondary hover:text-text-primary disabled:opacity-40 transition-colors"
                    >
                        {previewMutation.isPending
                            ? <Loader2 size={12} className="animate-spin" />
                            : "Preview"}
                    </button>
                )
                : (
                    <>
                        <span className="text-xs text-text-secondary">
                            {previewCount} item{previewCount !== 1 ? "s" : ""}
                            {" "}
                            affected
                        </span>
                        <button
                            type="button"
                            onClick={() => executeMutation.mutate()}
                            disabled={executeMutation.isPending ||
                                previewCount === 0}
                            className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-40 transition-colors"
                        >
                            {executeMutation.isPending
                                ? <Loader2 size={12} className="animate-spin" />
                                : "Confirm Remove"}
                        </button>
                    </>
                )}
            <button
                type="button"
                onClick={onClose}
                className="p-1 text-text-muted hover:text-text-primary transition-colors"
            >
                <X size={14} />
            </button>
        </div>
    );
};

// inline editor for the AI Description cell. Click description text → enter
// edit mode (input + save + cancel + trash). Saving an empty string is treated
// as "delete" to keep the action surface minimal.
const DescriptionCell = ({
    tagName,
    definition,
    isLoadingDefinitions,
}: {
    tagName: string;
    definition: TagDefinition | undefined;
    isLoadingDefinitions: boolean;
}): ReactElement => {
    const queryClient = useQueryClient();
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(
        definition?.ai_description ?? "",
    );

    const saveMutation = useMutation({
        mutationFn: () => splClient.setTagDefinition(tagName, editValue.trim()),
        onSuccess: () => {
            toast.success(`Definition saved for "${tagName}"`);
            setIsEditing(false);
            queryClient.invalidateQueries({
                queryKey: ["tagDefinitions"],
            });
        },
        onError: (error: Error) => toast.error(`Save failed: ${error.message}`),
    });

    const deleteMutation = useMutation({
        mutationFn: () => splClient.deleteTagDefinition(tagName),
        onSuccess: () => {
            toast.success(`Definition removed for "${tagName}"`);
            setEditValue("");
            setIsEditing(false);
            queryClient.invalidateQueries({
                queryKey: ["tagDefinitions"],
            });
        },
        onError: (error: Error) =>
            toast.error(`Delete failed: ${error.message}`),
    });

    const hasDefinition = Boolean(definition);

    if (isLoadingDefinitions && !definition) {
        return (
            <span className="text-xs text-text-muted italic">Loading...</span>
        );
    }

    if (isEditing) {
        return (
            <div className="flex items-center gap-2">
                <input
                    type="text"
                    value={editValue}
                    onChange={(event) =>
                        setEditValue((event.target as HTMLInputElement).value)}
                    placeholder="Describe what this tag means for AI..."
                    className="flex-1 px-2 py-1 text-sm bg-bg-tertiary border border-border rounded text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
                    autoFocus
                    onKeyDown={(event) => {
                        if (event.key === "Enter" && editValue.trim()) {
                            saveMutation.mutate();
                        }
                        if (event.key === "Escape") {
                            setIsEditing(false);
                            setEditValue(definition?.ai_description ?? "");
                        }
                    }}
                />
                <button
                    type="button"
                    onClick={() => saveMutation.mutate()}
                    disabled={!editValue.trim() || saveMutation.isPending}
                    className="p-1.5 rounded text-accent hover:bg-accent/20 disabled:opacity-40 transition-colors"
                    title="Save"
                >
                    {saveMutation.isPending
                        ? <Loader2 size={14} className="animate-spin" />
                        : <Save size={14} />}
                </button>
                <button
                    type="button"
                    onClick={() => {
                        setIsEditing(false);
                        setEditValue(definition?.ai_description ?? "");
                    }}
                    className="p-1.5 rounded text-text-muted hover:text-text-primary transition-colors"
                    title="Cancel"
                >
                    <X size={14} />
                </button>
                {hasDefinition && (
                    <button
                        type="button"
                        onClick={() => deleteMutation.mutate()}
                        disabled={deleteMutation.isPending}
                        className="p-1.5 rounded text-text-muted hover:text-red-400 hover:bg-bg-tertiary disabled:opacity-40 transition-colors"
                        title="Remove description"
                    >
                        {deleteMutation.isPending
                            ? <Loader2 size={14} className="animate-spin" />
                            : <Trash2 size={14} />}
                    </button>
                )}
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={() => setIsEditing(true)}
            className={`text-sm text-left w-full ${
                hasDefinition
                    ? "text-text-secondary hover:text-text-primary"
                    : "text-text-muted italic hover:text-text-secondary"
            } transition-colors`}
        >
            {definition?.ai_description ?? "Add description..."}
        </button>
    );
};

const TagStatsTable = ({
    tags,
    maxCount,
    totalItems,
    definitionsMap,
    isLoadingDefinitions,
}: {
    tags: TagStat[];
    maxCount: number;
    totalItems: number;
    definitionsMap: Map<string, TagDefinition>;
    isLoadingDefinitions: boolean;
}): ReactElement => {
    const [filter, setFilter] = useState("");
    const [sortField, setSortField] = useState<SortField>("count");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
    const [page, setPage] = useState(1);
    const [activeRowAction, setActiveRowAction] = useState<
        { tag: string; action: "rename" | "remove"; } | null
    >(null);

    const filteredAndSorted = useMemo(() => {
        const lowerFilter = filter.toLowerCase();
        const filtered = filter
            ? tags.filter((tagStat) =>
                tagStat.tag.toLowerCase().includes(lowerFilter)
            )
            : tags;

        return [...filtered].sort((left, right) => {
            const multiplier = sortDirection === "asc" ? 1 : -1;
            if (sortField === "tag") {
                return multiplier * left.tag.localeCompare(right.tag);
            }
            if (sortField === "description") {
                // length-based: longer descriptions are "greater" so asc surfaces undefined/short first.
                // tiebreak on tag name so the order is stable.
                const leftLen =
                    (definitionsMap.get(left.tag)?.ai_description ?? "").length;
                const rightLen =
                    (definitionsMap.get(right.tag)?.ai_description ?? "")
                        .length;
                if (leftLen !== rightLen) {
                    return multiplier * (leftLen - rightLen);
                }
                return left.tag.localeCompare(right.tag);
            }
            return multiplier * (left.count - right.count);
        });
    }, [tags, filter, sortField, sortDirection, definitionsMap]);

    const totalPages = Math.max(
        1,
        Math.ceil(filteredAndSorted.length / PAGE_SIZE),
    );
    const currentPage = Math.min(page, totalPages);
    const pageStart = (currentPage - 1) * PAGE_SIZE;
    const pageItems = filteredAndSorted.slice(pageStart, pageStart + PAGE_SIZE);

    // reset to page 1 when filter changes
    const handleFilterChange = (value: string): void => {
        setFilter(value);
        setPage(1);
    };

    const toggleSort = (field: SortField): void => {
        if (sortField === field) {
            setSortDirection((prev) => prev === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection(field === "count" ? "desc" : "asc");
        }
    };

    const renderSortIcon = (field: SortField): ReactElement | null => {
        if (sortField !== field) return null;
        return (
            <span className="text-accent text-xs ml-1">
                {sortDirection === "asc" ? "\u2191" : "\u2193"}
            </span>
        );
    };

    const ariaSortFor = (
        field: SortField,
    ): "ascending" | "descending" | "none" => {
        if (sortField !== field) return "none";
        return sortDirection === "asc" ? "ascending" : "descending";
    };

    return (
        <div className="bg-bg-secondary rounded-lg border border-border">
            <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <Tags size={18} className="text-accent" />
                        <h2 className="text-lg font-semibold">
                            Tag Statistics
                        </h2>
                        <span className="px-2 py-0.5 text-xs rounded-full bg-accent/20 text-accent">
                            {tags.length} tag{tags.length !== 1 ? "s" : ""}
                        </span>
                    </div>
                    <span className="text-sm text-text-secondary">
                        {totalItems} total items
                    </span>
                </div>
                <div className="relative">
                    <Search
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
                    />
                    <input
                        type="text"
                        value={filter}
                        onChange={(event) =>
                            handleFilterChange(
                                (event.target as HTMLInputElement).value,
                            )}
                        placeholder="Filter tags..."
                        className="w-full pl-9 pr-3 py-2 bg-bg-tertiary border border-border rounded text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm"
                    />
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-border text-text-secondary">
                            <th
                                scope="col"
                                data-sort-column="tag"
                                aria-sort={ariaSortFor("tag")}
                                className="text-left px-4 py-2 cursor-pointer hover:text-text-primary transition-colors select-none"
                                onClick={() => toggleSort("tag")}
                            >
                                <span className="flex items-center gap-1">
                                    Tag
                                    <ArrowUpDown
                                        size={12}
                                        className="text-text-muted"
                                    />
                                    {renderSortIcon("tag")}
                                </span>
                            </th>
                            <th
                                scope="col"
                                data-sort-column="count"
                                aria-sort={ariaSortFor("count")}
                                className="text-right px-4 py-2 cursor-pointer hover:text-text-primary transition-colors select-none w-24"
                                onClick={() => toggleSort("count")}
                            >
                                <span className="flex items-center justify-end gap-1">
                                    Count
                                    <ArrowUpDown
                                        size={12}
                                        className="text-text-muted"
                                    />
                                    {renderSortIcon("count")}
                                </span>
                            </th>
                            <th
                                scope="col"
                                className="text-left px-4 py-2 w-40"
                            >
                                Usage
                            </th>
                            <th
                                scope="col"
                                data-sort-column="description"
                                aria-sort={ariaSortFor("description")}
                                className="text-left px-4 py-2 cursor-pointer hover:text-text-primary transition-colors select-none"
                                onClick={() => toggleSort("description")}
                            >
                                <span className="flex items-center gap-1">
                                    AI Description
                                    <ArrowUpDown
                                        size={12}
                                        className="text-text-muted"
                                    />
                                    {renderSortIcon("description")}
                                </span>
                            </th>
                            <th
                                scope="col"
                                data-sort-column="actions"
                                className="text-right px-4 py-2 w-32"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageItems.length === 0
                            ? (
                                <tr>
                                    <td
                                        colSpan={5}
                                        className="px-4 py-8 text-center text-text-muted"
                                    >
                                        {filter
                                            ? "No tags match your filter"
                                            : "No tags found"}
                                    </td>
                                </tr>
                            )
                            : pageItems.map((tagStat) => {
                                // percentage bar width is relative to the most popular tag
                                const barWidth = maxCount > 0
                                    ? (tagStat.count / maxCount) * 100
                                    : 0;
                                const isActiveRename =
                                    activeRowAction?.tag === tagStat.tag &&
                                    activeRowAction.action === "rename";
                                const isActiveRemove =
                                    activeRowAction?.tag === tagStat.tag &&
                                    activeRowAction.action === "remove";

                                return (
                                    <tr
                                        key={tagStat.tag}
                                        className="border-b border-border/50 hover:bg-bg-tertiary/50 transition-colors"
                                    >
                                        <td className="px-4 py-2">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <Tag
                                                        size={12}
                                                        className="text-text-muted flex-shrink-0"
                                                    />
                                                    <span className="text-text-primary font-medium">
                                                        {tagStat.tag}
                                                    </span>
                                                </div>
                                                {isActiveRename && (
                                                    <InlineRenameForm
                                                        tag={tagStat.tag}
                                                        onClose={() =>
                                                            setActiveRowAction(
                                                                null,
                                                            )}
                                                    />
                                                )}
                                                {isActiveRemove && (
                                                    <InlineRemoveConfirm
                                                        tag={tagStat.tag}
                                                        onClose={() =>
                                                            setActiveRowAction(
                                                                null,
                                                            )}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 text-right text-text-secondary tabular-nums">
                                            {tagStat.count.toLocaleString()}
                                        </td>
                                        <td className="px-4 py-2">
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 h-2 bg-bg-tertiary rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-accent rounded-full transition-all"
                                                        style={{
                                                            width:
                                                                `${barWidth}%`,
                                                        }}
                                                    />
                                                </div>
                                                <span className="text-xs text-text-muted w-12 text-right tabular-nums">
                                                    {tagStat.percentage.toFixed(
                                                        1,
                                                    )}%
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 align-top">
                                            <DescriptionCell
                                                tagName={tagStat.tag}
                                                definition={definitionsMap.get(
                                                    tagStat.tag,
                                                )}
                                                isLoadingDefinitions={isLoadingDefinitions}
                                            />
                                        </td>
                                        <td className="px-4 py-2">
                                            <div className="flex items-center justify-end gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setActiveRowAction(
                                                            isActiveRename
                                                                ? null
                                                                : {
                                                                    tag: tagStat
                                                                        .tag,
                                                                    action:
                                                                        "rename",
                                                                },
                                                        )}
                                                    className={`p-1.5 rounded transition-colors ${
                                                        isActiveRename
                                                            ? "bg-accent/20 text-accent"
                                                            : "text-text-muted hover:text-text-primary hover:bg-bg-tertiary"
                                                    }`}
                                                    title="Rename tag"
                                                >
                                                    <Edit3 size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setActiveRowAction(
                                                            isActiveRemove
                                                                ? null
                                                                : {
                                                                    tag: tagStat
                                                                        .tag,
                                                                    action:
                                                                        "remove",
                                                                },
                                                        )}
                                                    className={`p-1.5 rounded transition-colors ${
                                                        isActiveRemove
                                                            ? "bg-red-500/20 text-red-400"
                                                            : "text-text-muted hover:text-red-400 hover:bg-bg-tertiary"
                                                    }`}
                                                    title="Remove tag from all items"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                    <span className="text-xs text-text-muted">
                        Showing {pageStart + 1}-{Math.min(
                            pageStart + PAGE_SIZE,
                            filteredAndSorted.length,
                        )} of {filteredAndSorted.length}
                    </span>
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => setPage(currentPage - 1)}
                            disabled={currentPage <= 1}
                            className="p-1 rounded text-text-secondary hover:text-text-primary disabled:opacity-40 transition-colors"
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <span className="px-2 text-xs text-text-secondary tabular-nums">
                            {currentPage} / {totalPages}
                        </span>
                        <button
                            type="button"
                            onClick={() => setPage(currentPage + 1)}
                            disabled={currentPage >= totalPages}
                            className="p-1 rounded text-text-secondary hover:text-text-primary disabled:opacity-40 transition-colors"
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const BulkOperationsForm = (): ReactElement => {
    const queryClient = useQueryClient();
    const [bulkAction, setBulkAction] = useState<BulkAction>("add");
    const [tag, setTag] = useState("");
    const [sourceTag, setSourceTag] = useState("");
    const [newTag, setNewTag] = useState("");
    const [mergeTags, setMergeTags] = useState("");
    const [previewCount, setPreviewCount] = useState<number | null>(null);

    const buildRequest = (): TagOperationRequest | null => {
        switch (bulkAction) {
            case "add": {
                const trimmedTag = tag.trim();
                const trimmedSource = sourceTag.trim();
                if (!trimmedTag || !trimmedSource) return null;
                return {
                    action: "add" as TagOperationAction,
                    tag: trimmedTag,
                    sourceTag: trimmedSource,
                };
            }
            case "remove_filtered": {
                const trimmedTag = tag.trim();
                const trimmedSource = sourceTag.trim();
                if (!trimmedTag || !trimmedSource) return null;
                return {
                    action: "remove" as TagOperationAction,
                    tag: trimmedTag,
                    sourceTag: trimmedSource,
                };
            }
            case "merge": {
                const parsedTags = mergeTags.split(",").map((entry) =>
                    entry.trim()
                ).filter(Boolean);
                const trimmedNew = newTag.trim();
                if (parsedTags.length < 2 || !trimmedNew) return null;
                return {
                    action: "merge" as TagOperationAction,
                    tags: parsedTags,
                    newTag: trimmedNew,
                };
            }
            default:
                return null;
        }
    };

    const request = buildRequest();

    const previewMutation = useMutation({
        mutationFn: (req: TagOperationRequest) =>
            splClient.previewTagOperation(req),
        onSuccess: (data) => setPreviewCount(data.affectedCount),
        onError: (error: Error) =>
            toast.error(`Preview failed: ${error.message}`),
    });

    const executeMutation = useMutation({
        mutationFn: (req: TagOperationRequest) =>
            splClient.executeTagOperation(req),
        onSuccess: (data) => {
            toast.success(data.message);
            queryClient.invalidateQueries({
                queryKey: ["tagStats"],
            });
            resetForm();
        },
        onError: (error: Error) =>
            toast.error(`Execute failed: ${error.message}`),
    });

    const resetForm = (): void => {
        setTag("");
        setSourceTag("");
        setNewTag("");
        setMergeTags("");
        setPreviewCount(null);
    };

    const handleActionChange = (action: BulkAction): void => {
        setBulkAction(action);
        resetForm();
    };

    const handlePreview = (): void => {
        if (request) previewMutation.mutate(request);
    };

    const handleExecute = (): void => {
        if (request) executeMutation.mutate(request);
    };

    const actionIcon = bulkAction === "add"
        ? Plus
        : bulkAction === "remove_filtered"
        ? Minus
        : Merge;
    const ActionIcon = actionIcon;

    return (
        <div className="bg-bg-secondary rounded-lg border border-border">
            <div className="p-4 border-b border-border">
                <div className="flex items-center gap-2">
                    <ActionIcon size={18} className="text-accent" />
                    <h2 className="text-lg font-semibold">Bulk Operations</h2>
                </div>
            </div>

            <div className="p-4 space-y-4">
                <div>
                    <label className="block text-sm text-text-secondary mb-1">
                        Operation
                    </label>
                    <select
                        value={bulkAction}
                        onChange={(event) =>
                            handleActionChange(
                                (event.target as HTMLInputElement)
                                    .value as BulkAction,
                            )}
                        className="w-full px-3 py-2 bg-bg-tertiary border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent"
                    >
                        {Object.entries(BULK_ACTION_LABELS).map((
                            [value, label],
                        ) => <option key={value} value={value}>{label}
                        </option>)}
                    </select>
                </div>

                {(bulkAction === "add" || bulkAction === "remove_filtered") && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-text-secondary mb-1">
                                {bulkAction === "add"
                                    ? "Tag to add"
                                    : "Tag to remove"}
                            </label>
                            <input
                                type="text"
                                value={tag}
                                onChange={(event) => {
                                    setTag(
                                        (event.target as HTMLInputElement)
                                            .value,
                                    );
                                    setPreviewCount(null);
                                }}
                                placeholder={bulkAction === "add"
                                    ? "e.g. games"
                                    : "e.g. cringe"}
                                className="w-full px-3 py-2 bg-bg-tertiary border border-border rounded text-text-primary placeholder:text-text-muted text-sm focus:outline-none focus:border-accent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-text-secondary mb-1">
                                From items tagged with
                            </label>
                            <input
                                type="text"
                                value={sourceTag}
                                onChange={(event) => {
                                    setSourceTag(
                                        (event.target as HTMLInputElement)
                                            .value,
                                    );
                                    setPreviewCount(null);
                                }}
                                placeholder="e.g. halo"
                                className="w-full px-3 py-2 bg-bg-tertiary border border-border rounded text-text-primary placeholder:text-text-muted text-sm focus:outline-none focus:border-accent"
                            />
                        </div>
                    </div>
                )}

                {bulkAction === "merge" && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-text-secondary mb-1">
                                Tags to merge (comma-separated)
                            </label>
                            <input
                                type="text"
                                value={mergeTags}
                                onChange={(event) => {
                                    setMergeTags(
                                        (event.target as HTMLInputElement)
                                            .value,
                                    );
                                    setPreviewCount(null);
                                }}
                                placeholder="e.g. halo, cod, fortnite"
                                className="w-full px-3 py-2 bg-bg-tertiary border border-border rounded text-text-primary placeholder:text-text-muted text-sm focus:outline-none focus:border-accent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-text-secondary mb-1">
                                Merge into tag
                            </label>
                            <input
                                type="text"
                                value={newTag}
                                onChange={(event) => {
                                    setNewTag(
                                        (event.target as HTMLInputElement)
                                            .value,
                                    );
                                    setPreviewCount(null);
                                }}
                                placeholder="e.g. games"
                                className="w-full px-3 py-2 bg-bg-tertiary border border-border rounded text-text-primary placeholder:text-text-muted text-sm focus:outline-none focus:border-accent"
                            />
                        </div>
                    </div>
                )}

                <div className="flex items-center gap-3 pt-2">
                    <button
                        type="button"
                        onClick={handlePreview}
                        disabled={!request || previewMutation.isPending}
                        className="flex items-center gap-2 px-4 py-2 bg-bg-tertiary border border-border rounded text-sm text-text-primary hover:bg-border disabled:opacity-40 transition-colors"
                    >
                        {previewMutation.isPending
                            ? <Loader2 size={14} className="animate-spin" />
                            : <Search size={14} />}
                        Preview
                    </button>

                    {previewCount !== null && (
                        <span className="text-sm text-text-secondary">
                            <Check
                                size={14}
                                className="inline mr-1 text-green-400"
                            />
                            {previewCount} item{previewCount !== 1 ? "s" : ""}
                            {" "}
                            will be affected
                        </span>
                    )}

                    <button
                        type="button"
                        onClick={handleExecute}
                        disabled={previewCount === null || previewCount === 0 ||
                            executeMutation.isPending}
                        className="flex items-center gap-2 px-4 py-2 bg-accent text-white rounded text-sm hover:bg-accent/80 disabled:opacity-40 transition-colors ml-auto"
                    >
                        {executeMutation.isPending
                            ? <Loader2 size={14} className="animate-spin" />
                            : <Check size={14} />}
                        Execute
                    </button>
                </div>
            </div>
        </div>
    );
};

export const TagsTab = (): ReactElement => {
    const {
        data: tagStatsData,
        isLoading: isLoadingStats,
    } = useQuery({
        queryKey: ["tagStats"],
        queryFn: () => splClient.getTagStats(),
    });

    const {
        data: definitionsData,
        isLoading: isLoadingDefinitions,
    } = useQuery({
        queryKey: ["tagDefinitions"],
        queryFn: () => splClient.getTagDefinitions(),
    });

    const definitionsMap = useMemo(() => {
        const map = new Map<string, TagDefinition>();
        for (const def of definitionsData?.definitions ?? []) {
            map.set(def.tag_name, def);
        }
        return map;
    }, [definitionsData]);

    if (isLoadingStats) {
        return (
            <div className="max-w-6xl mx-auto p-4">
                <div className="p-12 text-center text-text-secondary">
                    <Loader2
                        size={24}
                        className="mx-auto mb-3 animate-spin text-accent"
                    />
                    <p>Loading tag statistics...</p>
                </div>
            </div>
        );
    }

    const tags = tagStatsData?.tags ?? [];
    const totalItems = tagStatsData?.total_items ?? 0;
    const maxCount = tagStatsData?.max_count ?? 0;
    const untaggedCount = tagStatsData?.untagged_count ?? 0;
    const definedCount = definitionsMap.size;

    return (
        <div className="max-w-6xl mx-auto p-4 space-y-6">
            <div className="flex items-center gap-4 text-sm text-text-secondary flex-wrap">
                <span>
                    {tags.length} unique tag{tags.length !== 1 ? "s" : ""}
                </span>
                <span className="text-border">|</span>
                <span>{totalItems.toLocaleString()} total items</span>
                {untaggedCount > 0 && (
                    <>
                        <span className="text-border">|</span>
                        <span className="text-yellow-400">
                            {untaggedCount.toLocaleString()} untagged
                        </span>
                    </>
                )}
                <span className="text-border">|</span>
                <span>
                    {definedCount} of {tags.length} described for AI
                </span>
            </div>

            <TagStatsTable
                tags={tags}
                maxCount={maxCount}
                totalItems={totalItems}
                definitionsMap={definitionsMap}
                isLoadingDefinitions={isLoadingDefinitions}
            />

            <BulkOperationsForm />
        </div>
    );
};
